import { css } from '@emotion/react';
import { FC, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Country } from 'react-phone-number-input';

import { useOutsideClick } from '~src/commons/utils/hooks';
import { useFocus, useOutSideIframeClick } from '~src/commons/utils/hooks/checkout-hooks';
import { useFormFieldScroll } from '~src/commons/utils/hooks/use-form-field-scroll';
import { useGlobalState } from '~src/global-state';

import {
  VatFieldType,
  getVatFieldConfig,
  mapCountryCodeToVatType,
} from '~address-form/validation/vat-field-validation';

import { MaskedInput } from '../masked-input';
import { StandardInputProps } from '../standard-input';
import { VatSelect } from './vat-select';

interface VatInputProps extends Omit<StandardInputProps, 'name'> {}

export const VatInput: FC<VatInputProps> = props => {
  const globalState = useGlobalState();
  const { getValues, setValue } = useFormContext();
  const { focusProps, isFocused } = useFocus();

  const inputRef = useRef<HTMLInputElement>(null);
  useFormFieldScroll('vat', inputRef);
  const mouseDown = useRef(false);

  const purchaseCountry = globalState.purchase_country as Country;

  const [vatCode, setVatCode] = useState<VatFieldType>(() => {
    const vatType = getValues('vat_type');

    if (vatType) {
      return vatType;
    }

    const countryVatNumbers = mapCountryCodeToVatType(purchaseCountry);
    if (countryVatNumbers[0]) {
      return countryVatNumbers[0];
    } else {
      return 'other';
    }
  });

  const { inputType, ...maskConfig } = getVatFieldConfig(vatCode);

  useEffect(() => {
    setValue('vat_type', vatCode);
  }, [vatCode]);

  const handleVatTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setVatCode(e.target.value as VatFieldType);
    setValue('vat_type', e.target.value);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (!focusProps) {
      return;
    }
    focusProps.onFocus();
  };

  const handleBlur = () => {
    if (!focusProps || mouseDown.current) {
      handleFocus();
      mouseDown.current = false;
    } else {
      focusProps.onBlur();
    }
  };

  const wrapperRef = useOutsideClick(() => {
    handleBlur();
  }, [isFocused]);

  useOutSideIframeClick(() => {
    handleBlur();
  }, [isFocused]);

  return (
    <div css={styles.vatField} ref={wrapperRef}>
      <MaskedInput
        {...props}
        name="vat"
        maskOptions={maskConfig}
        onBlur={handleBlur}
        isFocused={isFocused}
        onFocus={handleFocus}
        type={inputType}
        onMouseDown={() => (mouseDown.current = true)}
        extensionElement={
          <VatSelect name="vat_type" vatCode={vatCode} onChange={handleVatTypeChange} />
        }
      />
    </div>
  );
};

const styles = {
  vatField: css({
    position: 'relative',
    flexGrow: 1,
  }),
  vatType: {
    marginLeft: '4px',
  },
};
